type RuntimeConfigKey =
  | 'VUE_APP_API_BASE_URL'
  | 'VUE_APP_SUPPORT_WHATSAPP_NUMBER';

type RuntimeConfigValue = boolean | number | string | null | undefined;

const runtimeConfig = (): Partial<Record<RuntimeConfigKey, RuntimeConfigValue>> => {
  if (typeof window === 'undefined') {
    return {};
  }

  return window.__SUITE241_RUNTIME_CONFIG__ || {};
};

const isConfigured = (value: RuntimeConfigValue): boolean =>
  value !== undefined && value !== null && String(value).trim() !== '';

const configValue = (key: RuntimeConfigKey, fallback?: string): RuntimeConfigValue => {
  const runtimeValue = runtimeConfig()[key];

  if (isConfigured(runtimeValue)) {
    return runtimeValue;
  }

  const envValue = process.env[key];

  if (isConfigured(envValue)) {
    return envValue;
  }

  return fallback;
};

const stringConfig = (key: RuntimeConfigKey, fallback = ''): string =>
  String(configValue(key, fallback) || fallback);

const positiveIntegerConfig = (key: RuntimeConfigKey, fallback: number): number => {
  const parsed = Number(configValue(key));

  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

/** Empêche une configuration oubliée de transmettre les identifiants en HTTP depuis le site HTTPS. */
export const resolveApiBaseUrl = (configured: string, pageUrl: string): string => {
  const page = new URL(pageUrl);
  const localPage = ['localhost', '127.0.0.1', '[::1]'].includes(page.hostname);
  const fallback = localPage && page.protocol === 'http:'
    ? 'http://127.0.0.1:2017/api/v1'
    : new URL('/api/v1', page).href;
  const api = new URL(configured.trim() || fallback, page);

  if (!['http:', 'https:'].includes(api.protocol) || api.username || api.password || api.search || api.hash) {
    throw new Error('L’adresse du service API est invalide.');
  }

  if (page.protocol === 'https:' && api.protocol !== 'https:') {
    throw new Error('L’adresse du service API doit utiliser HTTPS.');
  }

  return api.href.replace(/\/$/, '');
};

export const API_BASE_URL = resolveApiBaseUrl(
  stringConfig('VUE_APP_API_BASE_URL'),
  typeof window === 'undefined' ? 'http://localhost' : window.location.href
);

export const AUTH_TOKEN_STORAGE_KEY =
  process.env.VUE_APP_AUTH_TOKEN_STORAGE_KEY || 'suite241_gestion_commerciale_token';

export const AUTH_USER_STORAGE_KEY =
  process.env.VUE_APP_AUTH_USER_STORAGE_KEY || 'suite241_gestion_commerciale_user';

export const SHOP_ID_STORAGE_KEY =
  process.env.VUE_APP_SHOP_ID_STORAGE_KEY || 'suite241_gestion_commerciale_current_shop_id';

export const SHOP_INFO_STORAGE_KEY =
  process.env.VUE_APP_SHOP_INFO_STORAGE_KEY || 'suite241_gestion_commerciale_current_shop_info';

export const INVENTORY_DRAFT_STORAGE_PREFIX =
  process.env.VUE_APP_INVENTORY_DRAFT_STORAGE_PREFIX || 'suite241_gestion_commerciale_inventory_';

const normalizeWhatsAppNumber = (value: RuntimeConfigValue): string => {
  return String(value || '').replace(/\D/g, '');
};

export const WHATSAPP_SUPPORT_NUMBER = normalizeWhatsAppNumber(
  configValue('VUE_APP_SUPPORT_WHATSAPP_NUMBER')
);

const booleanConfig = (value: RuntimeConfigValue, fallback: boolean): boolean => {
  if (!isConfigured(value)) {
    return fallback;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  return ['1', 'true', 'yes', 'on'].includes(String(value).toLowerCase());
};

export const isMaintenanceModeEnabled = (): boolean => false;
